/**
 * PreferencesPageV2 — canonical-only filter editor.
 *
 * Cascading-dropdown rebuild of the legacy preferences page. The page
 * is split per vertical (auto/immo); each tab is a tree of
 * `SearchCombobox` (single) and `MultiSearchCombobox` (multi) widgets
 * that pull canonical option lists from `/api/taxonomy/...`.
 *
 * P0-B Sub-Step 2 (Batch 5b-1):
 *   * Multi-select via `MultiSearchCombobox`:
 *       - auto: makes, models, model_series, fuels, gearboxes
 *       - immo: object_types, postal_codes
 *   * Cascade is anchored on the FIRST entry of the multi-select.
 *     If `makes.length > 1`, the `models` field is disabled with a
 *     hint — we don't yet merge models across brands on the backend.
 *   * `immo.min_discount_pct` slider achieves parity with the legacy
 *     V1 page.
 *   * Backward-compat: legacy free-text values stored in
 *     `user_preferences` (e.g. `auto.models = ["Golf TGI"]`) are
 *     auto-migrated when an exact case-insensitive canonical match
 *     exists. Anything else lands in a "🚨 Nicht canonical"-pill so
 *     the user sees the value, can remove it, and is never silently
 *     stripped.
 *   * Cascading-reset: when the first entry of a parent multi-select
 *     changes, child selections (models for brand, series for model)
 *     are dropped to avoid stale combinations.
 *
 * Server contract unchanged — `user_preferences.auto.makes` etc. are
 * `list[str]`. We round-trip lists verbatim.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Save, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SearchCombobox } from "@/components/ui/SearchCombobox";
import { MultiSearchCombobox } from "@/components/ui/MultiSearchCombobox";
import { NonCanonicalPillList } from "@/components/ui/NonCanonicalPill";

import {
  getPreferences, putPreferences, resetPreferences, resetDealflowSeen,
} from "../api/dealflowApi";
import {
  getGeoCountries,
  getAutoBrands, getAutoModels,
  getAutoFuels, getAutoGearboxes,
  getAutoFilterOptions,
  getImmoRegions, getImmoCitiesCanonical, getImmoPostalCodes,
  getImmoObjectTypesCanonical, getImmoTransactionTypes,
} from "../api/taxonomyApi";
import { resolveCsvOrList } from "../lib/canonicalResolver";
import { enumLabel } from "../utils/enumLabels";

const TAX_STALE_MS = 5 * 60 * 1000; // 5 min — matches server cache TTL

// P0.3a (2026-08-02) — hidden immo object-type codes that must never
// reach the visible picker state, even if a legacy DB doc leaks them.
// Backend `/api/preferences` already sanitizes its response, but a
// stale cache / older backend deploy / older mobile client that
// pushed `land` back in would otherwise render a raw "land" pill or,
// worse, a "🚨 Nicht canonical" pill via the migration path. This
// second layer defends against both. Bump when the backend
// `IMMO_HIDDEN_OBJECT_TYPES` grows.
const HIDDEN_IMMO_OBJECT_TYPES = new Set(["land"]);

function sanitizeImmoObjectTypes(raw) {
  if (!Array.isArray(raw)) return raw;
  const seen = new Set();
  const out = [];
  for (const v of raw) {
    if (typeof v !== "string") continue;
    const s = v.trim();
    if (!s) continue;
    if (HIDDEN_IMMO_OBJECT_TYPES.has(s)) continue;
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

function sanitizeImmoPrefsBlock(immo) {
  if (!immo || typeof immo !== "object") return immo;
  if (!Array.isArray(immo.object_types)) return immo;
  return { ...immo, object_types: sanitizeImmoObjectTypes(immo.object_types) };
}

// ----------------------------------------------------------------------------
// Range-input helper
// ----------------------------------------------------------------------------
// ----------------------------------------------------------------------------
// V4.21 — Verkäufertyp multi-select pills (shared by auto + immo tabs).
function SellerTypePills({ value, options, onChange, testId }) {
  const { t } = useTranslation("preferences");
  const selected = value || [];
  const cls = (active) =>
    `rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
      active
        ? "border-amber-500/60 bg-amber-500/15 text-amber-300"
        : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-zinc-200"
    }`;
  const toggle = (slug) => {
    const next = selected.includes(slug)
      ? selected.filter((s) => s !== slug)
      : [...selected, slug];
    onChange(next);
  };
  return (
    <div>
      <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
        {t("sellerType.label")}
      </Label>
      <div className="flex flex-wrap gap-2" data-testid={testId}>
        <button type="button" data-testid={`${testId}-all`}
          onClick={() => onChange([])} className={cls(selected.length === 0)}>
          {t("sellerType.all")}
        </button>
        {options.map((slug) => (
          <button key={slug} type="button" data-testid={`${testId}-${slug}`}
            onClick={() => toggle(slug)} className={cls(selected.includes(slug))}>
            {enumLabel("seller_type", slug)}
          </button>
        ))}
      </div>
      {selected.length > 0 && (
        <p className="text-[11px] text-zinc-500 mt-1">{t("sellerType.hint")}</p>
      )}
    </div>
  );
}

function RangeInputRow({
  label, minValue, maxValue, onMinChange, onMaxChange,
  unit = "", placeholderMin = null, placeholderMax = null, testId,
}) {
  const { t } = useTranslation("preferences");
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">
        {label}{unit && <span className="text-muted-foreground ml-1">({unit})</span>}
      </Label>
      <div className="flex gap-2">
        <Input
          type="number" placeholder={placeholderMin ?? t("from")}
          value={minValue ?? ""}
          onChange={(e) => onMinChange(e.target.value === "" ? null : Number(e.target.value))}
          data-testid={testId ? `${testId}-min` : undefined}
          className="flex-1"
        />
        <Input
          type="number" placeholder={placeholderMax ?? t("to")}
          value={maxValue ?? ""}
          onChange={(e) => onMaxChange(e.target.value === "" ? null : Number(e.target.value))}
          data-testid={testId ? `${testId}-max` : undefined}
          className="flex-1"
        />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// AUTO cascade
// ----------------------------------------------------------------------------
function AutoPreferencesForm({ prefs, onChange, nonCanonical, removeNonCanonical }) {
  const { t } = useTranslation("preferences");
  const country = prefs.country || "AT";
  const auto = prefs.auto || {};

  // Cascade anchor: first entry of each multi-select.
  const anchorBrand = (auto.makes || [])[0] || "";
  const anchorModel = (auto.models || [])[0] || "";

  const countriesQ = useQuery({
    queryKey: ["taxonomy", "geo", "countries"],
    queryFn: getGeoCountries, staleTime: TAX_STALE_MS,
  });
  const brandsQ = useQuery({
    queryKey: ["taxonomy", "auto", "brands", country],
    queryFn: () => getAutoBrands(country), staleTime: TAX_STALE_MS,
  });
  const modelsQ = useQuery({
    queryKey: ["taxonomy", "auto", "models", anchorBrand, country],
    queryFn: () => getAutoModels(anchorBrand, country),
    enabled: !!anchorBrand, staleTime: TAX_STALE_MS,
  });
  const fuelsQ = useQuery({
    queryKey: ["taxonomy", "auto", "fuels"],
    queryFn: getAutoFuels, staleTime: TAX_STALE_MS,
  });
  const gearboxesQ = useQuery({
    queryKey: ["taxonomy", "auto", "gearboxes"],
    queryFn: getAutoGearboxes, staleTime: TAX_STALE_MS,
  });
  // V4.19 — canonical body types + feature slugs (one aggregated call).
  const filterOptionsQ = useQuery({
    queryKey: ["filter-options", "auto", country],
    queryFn: () => getAutoFilterOptions(country), staleTime: TAX_STALE_MS,
  });

  const setCountry = (v) => onChange({ ...prefs, country: v || null });
  const setAuto = (patch) => onChange({ ...prefs, auto: { ...auto, ...patch } });

  // Cascade-reset helpers — when the FIRST entry of a parent multi
  // changes, drop child selections so the user can't end up with
  // stale brand+model combos.
  const setMakes = (next) => {
    const nextAnchor = (next || [])[0] || "";
    const patch = { makes: next.length ? next : null };
    if (nextAnchor !== anchorBrand) {
      patch.models = null;
      patch.model_series = null;
    }
    setAuto(patch);
  };
  const setModels = (next) => {
    const nextAnchor = (next || [])[0] || "";
    const patch = { models: next.length ? next : null };
    if (nextAnchor !== anchorModel) {
      patch.model_series = null;
    }
    setAuto(patch);
  };
  // V4 UX: Generation/Serie ist bewusst KEIN UI-Filter mehr — intern
  // bleibt model_series für Market Engine & Comparables erhalten.
  const setFuels = (next) => setAuto({ fuels: next.length ? next : null });
  const setGearboxes = (next) => setAuto({ gearboxes: next.length ? next : null });
  // V4.19 — Karosserie (OR) + Ausstattung (AND) multi-selects.
  const setBodyTypes = (next) => setAuto({ body_types: next.length ? next : null });
  const setFeatures = (next) => setAuto({ features: next.length ? next : null });

  const tooManyBrands = (auto.makes || []).length > 1;
  const tooManyModels = (auto.models || []).length > 1;

  const brandOptions = (brandsQ.data?.items || []).map((b) => ({
    value: b.brand_key, label: b.brand_canonical, hint: b.country_origin,
  }));
  const modelOptions = (modelsQ.data?.items || []).map((m) => ({
    value: m.model_family, label: m.model_family,
  }));
  const fuelOptions = (fuelsQ.data?.items || []).map((f) => ({
    value: f.slug || f.label,
    label: enumLabel("fuel", f.slug || f.label),
  }));
  const gearboxOptions = (gearboxesQ.data?.items || []).map((g) => ({
    value: g.slug || g.label || g,
    label: enumLabel("gearbox", g.slug || g.label || g),
  }));
  const bodyTypeOptions = (filterOptionsQ.data?.body_types || []).map((b) => ({
    value: b.value, label: enumLabel("body_type", b.value),
  }));
  const featureOptions = (filterOptionsQ.data?.features || []).map((f) => ({
    value: f.value, label: enumLabel("features", f.value),
  }));

  return (
    <div className="space-y-4">
      {/* ───── Marke & Modell ─────────────────────────────────────── */}
      <section
        data-testid="auto-section-brand"
        className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5 lg:p-6 space-y-4"
      >
        <h3 className="text-xs uppercase tracking-widest text-zinc-500">
          {t("sections.brandModel")}
        </h3>

        <div>
          <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
            {t("fields.country")}
          </Label>
          <SearchCombobox
            value={country} onChange={setCountry}
            options={(countriesQ.data?.items || []).map((c) => ({
              value: c.code, label: `${c.code} · ${c.display_de}`,
            }))}
            placeholder={t("placeholders.country")}
            loading={countriesQ.isLoading}
            dataTestId="auto-country"
          />
        </div>

        <div>
          <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
            {t("fields.makes")}
          </Label>
          <MultiSearchCombobox
            value={auto.makes || []} onChange={setMakes}
            options={brandOptions}
            placeholder={brandsQ.isLoading ? t("placeholders.brandsLoading") : t("placeholders.brands")}
            loading={brandsQ.isLoading}
            dataTestId="auto-brand"
          />
          <NonCanonicalPillList
            field="Marke"
            values={nonCanonical.auto.makes}
            onRemove={(raw) => removeNonCanonical("auto", "makes", raw)}
            dataTestId="auto-brand-noncanonical"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
              {t("fields.models")}
            </Label>
            <MultiSearchCombobox
              value={auto.models || []} onChange={setModels}
              options={modelOptions}
              placeholder={
                tooManyBrands
                  ? t("placeholders.tooManyBrands")
                  : anchorBrand
                    ? t("placeholders.models")
                    : t("placeholders.modelsNeedBrand")
              }
              disabled={!anchorBrand || tooManyBrands}
              disabledReason={
                tooManyBrands
                  ? t("disabledReasons.tooManyBrands")
                  : t("disabledReasons.needBrand")
              }
              loading={modelsQ.isLoading}
              dataTestId="auto-model"
            />
            <NonCanonicalPillList
              field="Modell"
              values={nonCanonical.auto.models}
              onRemove={(raw) => removeNonCanonical("auto", "models", raw)}
              dataTestId="auto-model-noncanonical"
            />
          </div>
        </div>
      </section>

      {/* ───── Motor & Antrieb ────────────────────────────────────── */}
      <section
        data-testid="auto-section-drivetrain"
        className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5 lg:p-6 space-y-4"
      >
        <h3 className="text-xs uppercase tracking-widest text-zinc-500">
          {t("sections.drivetrain")}
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
              {t("fields.fuel")}
            </Label>
            <MultiSearchCombobox
              value={auto.fuels || []} onChange={setFuels}
              options={fuelOptions}
              placeholder={t("placeholders.fuel")}
              loading={fuelsQ.isLoading}
              dataTestId="auto-fuel"
            />
            <NonCanonicalPillList
              field={t("fields.fuel")}
              values={nonCanonical.auto.fuels}
              onRemove={(raw) => removeNonCanonical("auto", "fuels", raw)}
              dataTestId="auto-fuel-noncanonical"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
              {t("fields.gearbox")}
            </Label>
            <MultiSearchCombobox
              value={auto.gearboxes || []} onChange={setGearboxes}
              options={gearboxOptions}
              placeholder={t("placeholders.gearbox")}
              loading={gearboxesQ.isLoading}
              dataTestId="auto-gearbox"
            />
            <NonCanonicalPillList
              field={t("fields.gearbox")}
              values={nonCanonical.auto.gearboxes}
              onRemove={(raw) => removeNonCanonical("auto", "gearboxes", raw)}
              dataTestId="auto-gearbox-noncanonical"
            />
          </div>
        </div>
      </section>

      {/* ───── Karosserie & Ausstattung (V4.19) ───────────────────── */}
      <section
        data-testid="auto-section-body-features"
        className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5 lg:p-6 space-y-4"
      >
        <h3 className="text-xs uppercase tracking-widest text-zinc-500">
          {t("sections.bodyFeatures")}
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
              {t("fields.bodyType")}
            </Label>
            <MultiSearchCombobox
              value={auto.body_types || []} onChange={setBodyTypes}
              options={bodyTypeOptions}
              placeholder={t("fields.bodyType")}
              loading={filterOptionsQ.isLoading}
              dataTestId="auto-body-types"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
              {t("fields.features")}
            </Label>
            <MultiSearchCombobox
              value={auto.features || []} onChange={setFeatures}
              options={featureOptions}
              placeholder={t("fields.features")}
              loading={filterOptionsQ.isLoading}
              dataTestId="auto-features"
            />
          </div>
        </div>
        <p className="text-[11px] text-zinc-500" data-testid="auto-features-hint">
          {t("featuresHint")}
        </p>
        <SellerTypePills
          value={auto.seller_types}
          options={["private", "dealer"]}
          onChange={(next) => setAuto({ seller_types: next.length ? next : null })}
          testId="auto-seller-types"
        />
      </section>

      {/* ───── Preis & Eckdaten ───────────────────────────────────── */}
      <section
        data-testid="auto-section-numbers"
        className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5 lg:p-6 space-y-4"
      >
        <h3 className="text-xs uppercase tracking-widest text-zinc-500">
          {t("sections.priceNumbers")}
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <RangeInputRow label={t("fields.price")} unit="€"
            minValue={auto.price_min} maxValue={auto.price_max}
            onMinChange={(v) => setAuto({ price_min: v })}
            onMaxChange={(v) => setAuto({ price_max: v })}
            testId="auto-price"
          />
          <RangeInputRow label={t("fields.year")}
            minValue={auto.year_min} maxValue={auto.year_max}
            onMinChange={(v) => setAuto({ year_min: v })}
            onMaxChange={(v) => setAuto({ year_max: v })}
            placeholderMin={t("yearPlaceholderMin")} placeholderMax={t("yearPlaceholderMax")}
            testId="auto-year"
          />
          <RangeInputRow label={t("fields.mileage")} unit="km"
            minValue={auto.km_min} maxValue={auto.km_max}
            onMinChange={(v) => setAuto({ km_min: v })}
            onMaxChange={(v) => setAuto({ km_max: v })}
            testId="auto-km"
          />
          <RangeInputRow label={t("fields.power")} unit="PS"
            minValue={auto.power_ps_min} maxValue={auto.power_ps_max}
            onMinChange={(v) => setAuto({ power_ps_min: v })}
            onMaxChange={(v) => setAuto({ power_ps_max: v })}
            testId="auto-power-ps"
          />
        </div>

        <div>
          <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
            {t("fields.minDiscount")}
          </Label>
          <select
            value={auto.min_discount_pct ?? ""}
            onChange={(e) => setAuto({
              min_discount_pct: e.target.value === "" ? null : Number(e.target.value),
            })}
            data-testid="auto-min-discount-pct"
            className="h-10 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/60"
          >
            <option value="">{t("discount.default")}</option>
            {[5, 10, 15, 20, 25, 30].map((v) => (
              <option key={v} value={v}>{t("discount.option", { pct: v })}</option>
            ))}
          </select>
          <p className="text-[11px] text-zinc-500 mt-1">
            {t("discount.hint")}
          </p>
        </div>

        {/* P0.7b — Unfall-/Schadenfahrzeuge einbeziehen (DealFlow opt-in). */}
        <div className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-950/40 px-4 py-3">
          <div className="pr-4">
            <Label className="text-sm text-zinc-200">
              {t("includeDamaged.label")}
            </Label>
            <p className="text-xs text-zinc-500 mt-0.5">
              {t("includeDamaged.hint")}
            </p>
          </div>
          <Switch
            checked={auto.include_damaged === true}
            onCheckedChange={(checked) => setAuto({
              include_damaged: checked ? true : null,
            })}
            data-testid="auto-include-damaged"
          />
        </div>
      </section>
    </div>
  );
}

// ----------------------------------------------------------------------------
// IMMO cascade
// ----------------------------------------------------------------------------
function ImmoPreferencesForm({ prefs, onChange, nonCanonical, removeNonCanonical }) {
  const { t } = useTranslation("preferences");
  const country = prefs.country || "AT";
  const immo = prefs.immo || {};
  const tt = immo.transaction_type || "";
  const [state, setState] = useState(""); // not persisted yet (Batch 5)

  const ttQ = useQuery({
    queryKey: ["taxonomy", "immo", "tx-types"],
    queryFn: getImmoTransactionTypes, staleTime: TAX_STALE_MS,
  });
  const countriesQ = useQuery({
    queryKey: ["taxonomy", "geo", "countries"],
    queryFn: getGeoCountries, staleTime: TAX_STALE_MS,
  });
  const regionsQ = useQuery({
    queryKey: ["taxonomy", "immo", "regions", country],
    queryFn: () => getImmoRegions(country),
    enabled: !!country, staleTime: TAX_STALE_MS,
  });
  const citiesQ = useQuery({
    queryKey: ["taxonomy", "immo", "cities-canonical", country, state],
    queryFn: () => getImmoCitiesCanonical(country, state || null),
    enabled: !!country, staleTime: TAX_STALE_MS,
  });
  const postalsQ = useQuery({
    queryKey: ["taxonomy", "immo", "postal-codes", country, state],
    queryFn: () => getImmoPostalCodes(country, state || null),
    enabled: !!country && !!state, staleTime: TAX_STALE_MS,
  });
  // TC4 Recovery Iteration 2 — canonical-validation query for postal
  // codes. Runs as soon as a country is known (state-independent) so
  // chips can be flagged non-canonical BEFORE the user picks a
  // Bundesland. Without this the dropdown's cascade lock would
  // suppress validation and fake values would slip through.
  const postalsValidationQ = useQuery({
    queryKey: ["taxonomy", "immo", "postal-codes-validation", country],
    queryFn: () => getImmoPostalCodes(country, null),
    enabled: !!country, staleTime: TAX_STALE_MS,
  });
  const objTypesQ = useQuery({
    queryKey: ["taxonomy", "immo", "object-types-canonical", tt],
    queryFn: () => getImmoObjectTypesCanonical(tt || null),
    staleTime: TAX_STALE_MS,
  });

  const setImmo = (patch) => onChange({ ...prefs, immo: { ...immo, ...patch } });

  const city = immo.city || "";

  const setObjectTypes = (next) => setImmo({
    object_types: next.length ? next : null,
  });
  const setPostalCodes = (next) => setImmo({
    postal_codes: next.length ? next : null,
  });

  return (
    <div className="space-y-4">
      {/* ───── Art & Standort ─────────────────────────────────────── */}
      <section
        data-testid="immo-section-location"
        className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5 lg:p-6 space-y-4"
      >
        <h3 className="text-xs uppercase tracking-widest text-zinc-500">
          {t("sections.typeLocation")}
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
              {t("fields.transaction")}
            </Label>
            <SearchCombobox
              value={tt}
              onChange={(v) => setImmo({
                transaction_type: v || null,
                object_types: null,
              })}
              options={(ttQ.data?.items || []).map((x) => ({
                value: x.value || x.slug || x, label: x.label || x.value || x,
              }))}
              placeholder={t("placeholders.transaction")}
              loading={ttQ.isLoading}
              dataTestId="immo-tx-type"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
              Land
            </Label>
            <SearchCombobox
              value={country}
              onChange={(v) => {
                setState("");
                onChange({
                  ...prefs,
                  country: v || null,
                  immo: { ...immo, city: null, postal_codes: null },
                });
              }}
              options={(countriesQ.data?.items || []).map((c) => ({
                value: c.code, label: `${c.code} · ${c.display_de}`,
              }))}
              placeholder={t("placeholders.country")}
              loading={countriesQ.isLoading}
              dataTestId="immo-country"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
              {t("fields.state")}
            </Label>
            <SearchCombobox
              value={state}
              onChange={(v) => { setState(v); setImmo({ city: null, postal_codes: null }); }}
              options={(regionsQ.data?.items || []).map((r) => ({
                value: r.code, label: r.display_de,
              }))}
              placeholder={country ? t("placeholders.state") : t("placeholders.stateNeedCountry")}
              disabled={!country}
              disabledReason={t("disabledReasons.needCountry")}
              loading={regionsQ.isLoading}
              dataTestId="immo-state"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
              {t("fields.city")}
            </Label>
            <SearchCombobox
              value={city}
              onChange={(v) => setImmo({ city: v || null, postal_codes: null })}
              options={(citiesQ.data?.items || []).map((c) => ({
                value: c.display, label: c.display, hint: c.state_code,
              }))}
              placeholder={state ? t("placeholders.city") : t("placeholders.cityNeedState")}
              disabled={!country}
              disabledReason={t("disabledReasons.needCountry")}
              loading={citiesQ.isLoading}
              dataTestId="immo-city"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
            {t("fields.postalCodes")}
          </Label>
          <MultiSearchCombobox
            value={immo.postal_codes || []}
            onChange={setPostalCodes}
            options={(postalsQ.data?.items || []).map((p) => ({
              value: p.code, label: p.code, hint: p.state_code,
            }))}
            canonicalValues={
              // TC4 Recovery Iteration 2 — validate chips against the
              // FULL country-wide PLZ list, not the cascade-filtered
              // dropdown list. If the validation query hasn't returned
              // yet (or country is missing) we pass `undefined` so the
              // tri-state predicate falls back to "unknown" → no flag.
              postalsValidationQ.data?.items
                ? postalsValidationQ.data.items.map((p) => p.code)
                : undefined
            }
            placeholder={state ? t("placeholders.postal") : t("placeholders.cityNeedState")}
            disabled={!state}
            disabledReason={t("disabledReasons.needState")}
            loading={postalsQ.isLoading || postalsValidationQ.isLoading}
            dataTestId="immo-postal"
            maxVisible={300}
          />
          <NonCanonicalPillList
            field="PLZ"
            values={nonCanonical.immo.postal_codes}
            onRemove={(raw) => removeNonCanonical("immo", "postal_codes", raw)}
            dataTestId="immo-postal-noncanonical"
          />
        </div>
      </section>

      {/* ───── Objekt & Räume ─────────────────────────────────────── */}
      <section
        data-testid="immo-section-object"
        className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5 lg:p-6 space-y-4"
      >
        <h3 className="text-xs uppercase tracking-widest text-zinc-500">
          {t("sections.objectRooms")}
        </h3>

        <div>
          <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
            {t("fields.objectTypes")}
          </Label>
          <MultiSearchCombobox
            value={immo.object_types || []}
            onChange={setObjectTypes}
            options={(objTypesQ.data?.items || []).map((o) => ({
              value: o.code, label: o.display_de,
            }))}
            placeholder={t("placeholders.objectTypes")}
            loading={objTypesQ.isLoading}
            dataTestId="immo-object-type"
          />
          <NonCanonicalPillList
            field={t("fields.objectTypes")}
            values={nonCanonical.immo.object_types}
            onRemove={(raw) => removeNonCanonical("immo", "object_types", raw)}
            dataTestId="immo-object-type-noncanonical"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <RangeInputRow label={t("fields.livingArea")} unit="m²"
            minValue={immo.m2_min} maxValue={immo.m2_max}
            onMinChange={(v) => setImmo({ m2_min: v })}
            onMaxChange={(v) => setImmo({ m2_max: v })}
            testId="immo-m2"
          />
          <RangeInputRow label={t("fields.rooms")}
            minValue={immo.rooms_min} maxValue={immo.rooms_max}
            onMinChange={(v) => setImmo({ rooms_min: v })}
            onMaxChange={(v) => setImmo({ rooms_max: v })}
            testId="immo-rooms"
          />
        </div>

        <div className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-950/40 px-4 py-3">
          <div className="pr-4">
            <Label className="text-sm text-zinc-200">
              {t("fields.outdoor")}
            </Label>
            <p className="text-xs text-zinc-500 mt-0.5">
              {t("fields.outdoorHint")}
            </p>
          </div>
          <Switch
            checked={immo.has_outdoor_space === true}
            onCheckedChange={(checked) => setImmo({
              has_outdoor_space: checked ? true : null,
            })}
            data-testid="immo-outdoor"
          />
        </div>

        <SellerTypePills
          value={immo.seller_types}
          options={["private", "broker", "developer"]}
          onChange={(next) => setImmo({ seller_types: next.length ? next : null })}
          testId="immo-seller-types"
        />
      </section>

      {/* ───── Budget ─────────────────────────────────────────────── */}
      <section
        data-testid="immo-section-budget"
        className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5 lg:p-6 space-y-4"
      >
        <h3 className="text-xs uppercase tracking-widest text-zinc-500">
          {t("sections.budget")}
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {tt === "rent" ? (
            <RangeInputRow label={t("fields.rent")} unit="€"
              minValue={immo.rent_min} maxValue={immo.rent_max}
              onMinChange={(v) => setImmo({ rent_min: v })}
              onMaxChange={(v) => setImmo({ rent_max: v })}
              testId="immo-rent"
            />
          ) : (
            <RangeInputRow label={t("fields.buyPrice")} unit="€"
              minValue={immo.price_min} maxValue={immo.price_max}
              onMinChange={(v) => setImmo({ price_min: v })}
              onMaxChange={(v) => setImmo({ price_max: v })}
              testId="immo-price"
            />
          )}
          <div>
            <Label className="text-xs font-medium text-zinc-400 mb-1.5 block">
              {t("fields.minDiscount")}
            </Label>
            <select
              value={immo.min_discount_pct ?? ""}
              onChange={(e) => setImmo({
                min_discount_pct: e.target.value === "" ? null : Number(e.target.value),
              })}
              data-testid="immo-min-discount-pct"
              className="h-10 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/60"
            >
              <option value="">{t("discount.default")}</option>
              {[5, 10, 15, 20, 25, 30].map((v) => (
                <option key={v} value={v}>{t("discount.option", { pct: v })}</option>
              ))}
            </select>
            <p className="text-[11px] text-zinc-500 mt-1">
              {t("discount.hint")}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Page shell — orchestrates save/reset/cascade-migration
// ----------------------------------------------------------------------------
const EMPTY_NONCANONICAL = {
  auto: { makes: [], models: [], model_series: [], fuels: [], gearboxes: [] },
  immo: { object_types: [], postal_codes: [] },
};

export default function PreferencesPageV2() {
  const { t } = useTranslation("preferences");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const verticalParam = params.get("vertical") || "auto";
  const vertical = verticalParam === "immo" ? "immo" : "auto";

  // Local working copy — server is source of truth on first load.
  const [draft, setDraft] = useState({ country: "AT", auto: {}, immo: {} });
  const [dirty, setDirty] = useState(false);
  const [migrated, setMigrated] = useState(false);
  const [nonCanonical, setNonCanonical] = useState(EMPTY_NONCANONICAL);

  const prefsQ = useQuery({
    queryKey: ["preferences"],
    queryFn: getPreferences, staleTime: 0,
  });

  // Pull canonical option lists used by the auto-migration pass.
  const country = draft.country || "AT";
  const brandsQ = useQuery({
    queryKey: ["taxonomy", "auto", "brands", country],
    queryFn: () => getAutoBrands(country),
    enabled: !!prefsQ.data, staleTime: TAX_STALE_MS,
  });
  const fuelsQ = useQuery({
    queryKey: ["taxonomy", "auto", "fuels"],
    queryFn: getAutoFuels,
    enabled: !!prefsQ.data, staleTime: TAX_STALE_MS,
  });
  const gearboxesQ = useQuery({
    queryKey: ["taxonomy", "auto", "gearboxes"],
    queryFn: getAutoGearboxes,
    enabled: !!prefsQ.data, staleTime: TAX_STALE_MS,
  });
  const objTypesQ = useQuery({
    queryKey: ["taxonomy", "immo", "object-types-canonical-migration"],
    queryFn: () => getImmoObjectTypesCanonical(null),
    enabled: !!prefsQ.data, staleTime: TAX_STALE_MS,
  });

  // Initial sync of server prefs → local draft.
  useEffect(() => {
    if (prefsQ.data && !dirty) {
      setDraft({
        country: prefsQ.data.country || "AT",
        auto: prefsQ.data.auto || {},
        // P0.3a — strip hidden object types (currently `land`) before
        // they reach the visible picker state; keeps a legacy DB doc
        // from surfacing a raw "land" chip.
        immo: sanitizeImmoPrefsBlock(prefsQ.data.immo || {}),
      });
    }
  }, [prefsQ.data, dirty]);

  // Auto-migrate legacy values once prefs + the foundation taxonomy
  // lists have arrived. We only migrate the lists that don't depend
  // on a parent cascade (brands, fuels, gearboxes, object_types).
  // models/model_series/postal_codes depend on a parent (brand,
  // brand+model, state) and are migrated lazily once their lists are
  // available — see effect below this one.
  useEffect(() => {
    if (
      !prefsQ.data || migrated ||
      brandsQ.isLoading || fuelsQ.isLoading ||
      gearboxesQ.isLoading || objTypesQ.isLoading
    ) return;

    const brandOpts = (brandsQ.data?.items || []).map((b) => ({
      value: b.brand_key, label: b.brand_canonical,
    }));
    const fuelOpts = (fuelsQ.data?.items || []).map((f) => ({
      value: f.slug || f.label, label: f.label || f.slug,
    }));
    const gearboxOpts = (gearboxesQ.data?.items || []).map((g) => ({
      value: g.slug || g.label || g, label: g.label || g.slug || g,
    }));
    const objOpts = (objTypesQ.data?.items || []).map((o) => ({
      value: o.code, label: o.display_de,
    }));

    const auto = prefsQ.data.auto || {};
    // P0.3a — feed the migration path the STRIPPED list so a legacy
    // `land` value doesn't get shunted into the "🚨 Nicht canonical"
    // pill (which was the exact user-facing regression path).
    const immo = sanitizeImmoPrefsBlock(prefsQ.data.immo || {});

    const makesR = resolveCsvOrList(auto.makes, brandOpts);
    const fuelsR = resolveCsvOrList(auto.fuels, fuelOpts);
    const gearboxesR = resolveCsvOrList(auto.gearboxes, gearboxOpts);
    const objTypesR = resolveCsvOrList(immo.object_types, objOpts);

    setNonCanonical((prev) => ({
      ...prev,
      auto: {
        ...prev.auto,
        makes: makesR.nonCanonical,
        fuels: fuelsR.nonCanonical,
        gearboxes: gearboxesR.nonCanonical,
      },
      immo: {
        ...prev.immo,
        object_types: objTypesR.nonCanonical,
      },
    }));

    const migrationDelta = {};
    if (makesR.migratedCount > 0 || makesR.nonCanonical.length === 0) {
      migrationDelta.auto = {
        ...(migrationDelta.auto || {}),
        makes: makesR.canonicalList.length ? makesR.canonicalList : null,
      };
    }
    if (fuelsR.migratedCount > 0 || fuelsR.nonCanonical.length === 0) {
      migrationDelta.auto = {
        ...(migrationDelta.auto || {}),
        fuels: fuelsR.canonicalList.length ? fuelsR.canonicalList : null,
      };
    }
    if (gearboxesR.migratedCount > 0 || gearboxesR.nonCanonical.length === 0) {
      migrationDelta.auto = {
        ...(migrationDelta.auto || {}),
        gearboxes: gearboxesR.canonicalList.length ? gearboxesR.canonicalList : null,
      };
    }
    if (objTypesR.migratedCount > 0 || objTypesR.nonCanonical.length === 0) {
      migrationDelta.immo = {
        ...(migrationDelta.immo || {}),
        object_types: objTypesR.canonicalList.length ? objTypesR.canonicalList : null,
      };
    }

    if (Object.keys(migrationDelta).length > 0) {
      setDraft((d) => ({
        ...d,
        auto: { ...d.auto, ...(migrationDelta.auto || {}) },
        immo: { ...d.immo, ...(migrationDelta.immo || {}) },
      }));
      // Migration is silent (doesn't dirty the draft) per User-CTO call:
      // when the user opens the page and we silently align their
      // canonical values, they shouldn't be forced to re-save unless
      // they actively change something.
    }

    setMigrated(true);
  }, [
    prefsQ.data,
    brandsQ.data, fuelsQ.data, gearboxesQ.data, objTypesQ.data,
    brandsQ.isLoading, fuelsQ.isLoading, gearboxesQ.isLoading, objTypesQ.isLoading,
    migrated,
  ]);

  const saveMut = useMutation({
    mutationFn: () => putPreferences(draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["preferences"] });
      // Preferences drive the DealFlow query (min_discount_pct,
      // country, ranges…). Without this invalidation the dashboard
      // returned stale cached cards after a save (react-query
      // staleTime = 30s).
      qc.invalidateQueries({ queryKey: ["dealflow"] });
      toast.success(t("toastSaved"));
      setDirty(false);
      navigate(`/dashboard?refresh=${Date.now()}&vertical=${vertical}`);
    },
    onError: (e) => toast.error(t("toastSaveFailed", { message: e?.message || e })),
  });

  const resetMut = useMutation({
    mutationFn: () => resetPreferences(vertical),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["preferences"] });
      qc.invalidateQueries({ queryKey: ["dealflow"] });
      setDraft((d) => ({ ...d, [vertical]: {} }));
      setNonCanonical((prev) => ({
        ...prev,
        [vertical]: EMPTY_NONCANONICAL[vertical],
      }));
      setDirty(false);
      toast.success(t("toastReset"));
    },
  });

  const handleChange = (next) => {
    setDraft(next);
    setDirty(true);
  };

  const removeNonCanonical = (vertKey, fieldKey, raw) => {
    setNonCanonical((prev) => ({
      ...prev,
      [vertKey]: {
        ...prev[vertKey],
        [fieldKey]: (prev[vertKey][fieldKey] || []).filter((x) => x !== raw),
      },
    }));
    setDirty(true);
  };

  return (
    <div
      data-testid="preferences-page"
      className="mx-auto max-w-6xl px-4 lg:px-8 py-6 space-y-6"
    >
      {/* ───── Top bar: back-link · subtitle · reset ────────────── */}
      <div className="flex items-center justify-between gap-3">
        <Link
          to={`/dashboard?vertical=${vertical}`}
          className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-100 transition"
          data-testid="back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{t("backToDashboard")}</span>
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm(t("resetDealflowConfirm"))) return;
              try {
                await resetDealflowSeen();
                toast.success(t("resetDealflowDone"));
              } catch {
                toast.error(t("resetDealflowFailed"));
              }
            }}
            data-testid="reset-dealflow-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-1.5 text-sm text-zinc-200 hover:border-emerald-500/60 hover:text-emerald-300 transition"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("resetDealflow")}
          </button>
          <button
            type="button"
            onClick={() => resetMut.mutate()}
            disabled={resetMut.isPending}
            data-testid="reset-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-1.5 text-sm text-zinc-200 hover:border-amber-500/60 hover:text-amber-300 disabled:opacity-50 transition"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("reset")}
          </button>
        </div>
      </div>

      {/* ───── Heading + amber-pill vertical switcher ───────────── */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl text-zinc-100 font-medium tracking-tight">
            {t("title")}
          </h1>
          <p className="mt-1 text-sm text-zinc-400">
            {t("subtitle")}
          </p>
        </div>
        <div className="inline-flex rounded-xl border border-zinc-800 bg-zinc-950 p-1 self-start sm:self-auto">
          <button
            type="button"
            data-testid="tab-auto"
            onClick={() => navigate("/preferences?vertical=auto")}
            className={`px-5 py-2 text-sm rounded-lg transition ${
              vertical === "auto"
                ? "bg-amber-500 text-zinc-950 font-medium"
                : "text-zinc-300 hover:text-zinc-100"
            }`}
          >
            {t("tabAuto")}
          </button>
          <button
            type="button"
            data-testid="tab-immo"
            onClick={() => navigate("/preferences?vertical=immo")}
            className={`px-5 py-2 text-sm rounded-lg transition ${
              vertical === "immo"
                ? "bg-amber-500 text-zinc-950 font-medium"
                : "text-zinc-300 hover:text-zinc-100"
            }`}
          >
            {t("tabImmo")}
          </button>
        </div>
      </div>

      {/* ───── Form sections ────────────────────────────────────── */}
      {prefsQ.isLoading ? (
        <div
          data-testid="preferences-loading"
          className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-6 text-sm text-zinc-500"
        >
          {t("loading")}
        </div>
      ) : vertical === "auto" ? (
        <AutoPreferencesForm
          prefs={draft}
          onChange={handleChange}
          nonCanonical={nonCanonical}
          removeNonCanonical={removeNonCanonical}
        />
      ) : (
        <ImmoPreferencesForm
          prefs={draft}
          onChange={handleChange}
          nonCanonical={nonCanonical}
          removeNonCanonical={removeNonCanonical}
        />
      )}

      {/* ───── Sticky save bar ──────────────────────────────────── */}
      <div className="sticky bottom-4 z-30 pt-2">
        <div className="mx-auto rounded-xl border border-zinc-800 bg-zinc-950/90 backdrop-blur px-4 py-3 shadow-lg flex items-center justify-between gap-4">
          <span
            data-testid="save-status"
            className="text-xs text-zinc-400"
          >
            {dirty
              ? t("unsaved")
              : saveMut.isPending
                ? t("saving")
                : t("allSaved")}
          </span>
          <button
            type="button"
            disabled={!dirty || saveMut.isPending}
            onClick={() => saveMut.mutate()}
            data-testid="save-btn"
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${
              !dirty || saveMut.isPending
                ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                : "bg-amber-500 text-zinc-950 hover:bg-amber-400"
            }`}
          >
            <Save className="h-4 w-4" />
            {saveMut.isPending
              ? t("saveBusy")
              : dirty
                ? t("save")
                : t("savedBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}
